import axios from "axios";
import api from "./api";
import { TipoVeiculo } from "./constantes";

export interface FipeMarca {
    codigo: string;
    nome: string;
}

export interface FipeModelo {
    codigo: string;
    nome: string;
}

export interface FipeAno {
    codigo: string;
    nome: string;
}

export interface FipeValor {
    Valor: string;
    Marca: string;
    Modelo: string;
    AnoModelo: number;
    Combustivel: string;
    CodigoFipe: string;
    MesReferencia: string;
}

export interface ConsultaPlacaResponse {
    marca: string;
    modelo: string;
    ano: string;
    anoModelo: string;
    cor: string;
    tipoVeiculo: string;
    chassi: string;
    uf: string;
    municipio: string;
    valorFipe: number | null;
    modeloFipe: string;
    codigoFipe: string;
    sucesso: boolean;
    mensagem: string;
}

export async function consultarPorPlaca(placa: string): Promise<ConsultaPlacaResponse> {
    const placaLimpa = placa.replace('-', '').trim().toUpperCase();
    const response = await api.get<ConsultaPlacaResponse>(`/ConsultarPlaca/${placaLimpa}`);
    return response.data;
}

export function calcularVencimentoLicenciamento(placa: string): Date | null {
    const placaLimpa = placa.replace('-', '').trim();
    if (placaLimpa.length < 7) return null;

    const ultimoDigito = parseInt(placaLimpa[placaLimpa.length - 1]);
    if (isNaN(ultimoDigito)) return null;

    const mesesPorDigito: Record<number, number> = {
        1: 3,  // Abril (mês 3, zero-indexed)
        2: 4,  // Maio
        3: 5,  // Junho
        4: 6,  // Julho
        5: 7,  // Agosto
        6: 8,  // Setembro
        7: 9,  // Outubro
        8: 10, // Novembro
        9: 11, // Dezembro
        0: 0,  // Janeiro (próximo ano)
    };

    const mes = mesesPorDigito[ultimoDigito];
    const hoje = new Date();
    let ano = hoje.getFullYear();

    if (ultimoDigito === 0) {
        ano = ano + 1;
    } else if (mes < hoje.getMonth()) {
        ano = ano + 1;
    }

    const ultimoDiaDoMes = new Date(ano, mes + 1, 0).getDate();
    return new Date(ano, mes, ultimoDiaDoMes);
}

export class FipeService {
    private readonly baseURL = 'https://parallelum.com.br/fipe/api/v1';
    private readonly cache: Map<string, any> = new Map();
    private readonly axiosInstance = axios.create({
        baseURL: this.baseURL,
        timeout: 10000,
    });

    private getEndpoint(tipoVeiculo: TipoVeiculo): string {
        const endpoints: { [key in TipoVeiculo]?: string } = {
            [TipoVeiculo.tpCarro]: 'carros',
            [TipoVeiculo.tpMoto]: 'motos',
            [TipoVeiculo.tpCaminhao]: 'caminhoes',
        };
        return endpoints[tipoVeiculo] || '';
    }

    private async retryRequest<T>(fn: () => Promise<T>, retries = 2): Promise<T> {
        try {
            return await fn();
        } catch (error) {
            if (retries > 0) {
                await new Promise(resolve => setTimeout(resolve, 1000));
                return this.retryRequest(fn, retries - 1);
            }
            throw error;
        }
    }

    async getMarcas(tipoVeiculo: TipoVeiculo): Promise<FipeMarca[]> {
        if (!tipoVeiculo) throw new Error('Tipo de veículo é obrigatório');

        const cacheKey = `marcas_${tipoVeiculo}`;
        if (this.cache.has(cacheKey)) {
            return this.cache.get(cacheKey);
        }

        const endpoint = this.getEndpoint(tipoVeiculo);
        const response = await this.retryRequest(() =>
            this.axiosInstance.get<FipeMarca[]>(`${endpoint}/marcas`)
        );

        const marcas = response.data.map(marca => ({
            codigo: marca.codigo,
            nome: marca.nome
        }));

        this.cache.set(cacheKey, marcas);
        return marcas;
    }

    async getModelos(tipoVeiculo: TipoVeiculo, marcaCodigo: string): Promise<FipeModelo[]> {
        if (!tipoVeiculo || !marcaCodigo) throw new Error('Tipo de veículo e código da marca são obrigatórios');

        const cacheKey = `modelos_${tipoVeiculo}_${marcaCodigo}`;
        if (this.cache.has(cacheKey)) {
            return this.cache.get(cacheKey);
        }

        const endpoint = this.getEndpoint(tipoVeiculo);
        const response = await this.retryRequest(() =>
            this.axiosInstance.get<{ modelos: FipeModelo[] }>(`${endpoint}/marcas/${marcaCodigo}/modelos`)
        );

        const modelos = response.data.modelos.map(modelo => ({
            codigo: modelo.codigo,
            nome: modelo.nome
        }));

        this.cache.set(cacheKey, modelos);
        return modelos;
    }

    async getAnos(tipoVeiculo: TipoVeiculo, marcaCodigo: string, modeloCodigo: string): Promise<FipeAno[]> {
        if (!tipoVeiculo || !marcaCodigo || !modeloCodigo) throw new Error('Tipo de veículo, código da marca e código do modelo são obrigatórios');

        const cacheKey = `anos_${tipoVeiculo}_${marcaCodigo}_${modeloCodigo}`;
        if (this.cache.has(cacheKey)) {
            return this.cache.get(cacheKey);
        }

        const endpoint = this.getEndpoint(tipoVeiculo);
        const response = await this.retryRequest(() =>
            this.axiosInstance.get<FipeAno[]>(`${endpoint}/marcas/${marcaCodigo}/modelos/${modeloCodigo}/anos`)
        );

        const anos = response.data.map(ano => ({
            codigo: ano.codigo,
            nome: ano.nome
        }));

        this.cache.set(cacheKey, anos);
        return anos;
    }

    async getValor(tipoVeiculo: TipoVeiculo, marcaCodigo: string, modeloCodigo: string, anoCodigo: string): Promise<FipeValor> {
        if (!tipoVeiculo || !marcaCodigo || !modeloCodigo || !anoCodigo) throw new Error('Todos os parâmetros são obrigatórios');

        const cacheKey = `valor_${tipoVeiculo}_${marcaCodigo}_${modeloCodigo}_${anoCodigo}`;
        if (this.cache.has(cacheKey)) {
            return this.cache.get(cacheKey);
        }

        const endpoint = this.getEndpoint(tipoVeiculo);
        const response = await this.retryRequest(() =>
            this.axiosInstance.get<FipeValor>(`${endpoint}/marcas/${marcaCodigo}/modelos/${modeloCodigo}/anos/${anoCodigo}`)
        );

        this.cache.set(cacheKey, response.data);
        return response.data;
    }
}